import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  calculateInbound,
  calculateOutbound,
  isInvoicePaidInFull,
  type InvoiceKind,
  type InvoiceLineInput
} from '@qlkho-v3/shared';

import { api } from './api';
import { AsyncSelect, type OptionItem } from './components';
import { deliveryStatusOptions } from './delivery-status';
import { ArrowDownIcon, ArrowUpIcon, CloseIcon, PlusIcon, TrashIcon } from './icons';
import { productDiscountFromOption, resolveProductDiscount } from './invoice-product-discount';
import { productSecondaryText, visibleProductDetailName } from './product-display';
import { allocateLots, loadLots, notifyParent, type CrmPrefill, type PrefillLot } from './crm-prefill';

interface EditorLine extends InvoiceLineInput {
  key: string;
  productName: string;
  detailName: string;
  unitName?: string;
  availableQuantity?: number;
  inPrice?: number;
  minPrice?: number;
  warehouseName?: string;
  discountTypeProductLabel?: string;
  discountTypeProductPercent?: number;
}

interface EditorState {
  partnerId?: number;
  partnerLabel: string;
  discountTypeId?: number;
  discountTypeLabel: string;
  shipperId?: number;
  shipperLabel: string;
  saleId?: number;
  saleLabel: string;
  date: string;
  deliveryDate: string;
  discountValue: number;
  paid: number;
  paidDebt: number;
  vat: number;
  deliveryStatus: number;
  note: string;
  updatedAt?: string;
  updateBlockedReason: string;
  lines: EditorLine[];
}

type FieldErrors = Record<string, string>;

function today(): string {
  return new Date(Date.now() + 7 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

function initialState(): EditorState {
  return {
    partnerLabel: '',
    discountTypeLabel: '',
    shipperLabel: '',
    saleLabel: '',
    date: today(),
    deliveryDate: '',
    discountValue: 0,
    paid: 0,
    paidDebt: 0,
    vat: 0,
    deliveryStatus: 0,
    note: '',
    updateBlockedReason: '',
    lines: []
  };
}

function money(value: number): string {
  return new Intl.NumberFormat('vi-VN').format(value || 0);
}

function percent(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return '';
  return new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 2 }).format(value);
}

function productDiscountLabel(label: string | undefined, value: number | undefined): string {
  if (!label) return '';
  const percentage = percent(value);
  return percentage ? `${label} · Mặc định ${percentage}%` : label;
}

function numeric(value: string): number {
  return Number(value.replace(/[^0-9]/g, '')) || 0;
}

function numberField(
  value: number,
  onChange: (value: number) => void,
  label: string,
  options: { money?: boolean; min?: number; max?: number; error?: string; list?: string; onFocus?: () => void } = {}
) {
  return (
    <input
      className={options.money ? 'money-input' : ''}
      inputMode="numeric"
      aria-label={label}
      value={options.money ? money(value) : value}
      min={options.min}
      max={options.max}
      list={options.list}
      aria-invalid={Boolean(options.error)}
      onFocus={options.onFocus}
      onChange={(event) => onChange(options.money ? numeric(event.target.value) : Number(event.target.value) || 0)}
    />
  );
}

export function InvoiceEditor({
  kind,
  invoiceId,
  prefill,
  onClose,
  onSaved
}: {
  kind: InvoiceKind;
  invoiceId?: string;
  prefill?: CrmPrefill;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const outbound = kind === 'outbound';
  const [state, setState] = useState(initialState);
  const [prefillInfo, setPrefillInfo] = useState<{ code: string; warnings: string[]; loading: boolean } | null>(
    prefill && !invoiceId ? { code: prefill.code, warnings: [], loading: true } : null
  );

  // BUOC523: dien san tu don nhap CRM (ke toan do lai roi bam Luu)
  useEffect(() => {
    if (!prefill || invoiceId || !outbound) return;
    let cancelled = false;
    setState((current) => ({
      ...current,
      partnerId: prefill.customerId,
      partnerLabel: prefill.customerName ?? '',
      saleId: prefill.saleId,
      saleLabel: prefill.saleName ?? '',
      deliveryDate: prefill.deliveryDate ?? '',
      deliveryStatus: prefill.deliveryStatus ?? 0,
      discountValue: Number(prefill.discountValue ?? 0),
      paid: Number(prefill.paid ?? 0),
      note: prefill.note ?? ''
    }));
    void (async () => {
      const warnings: string[] = [];
      if (!prefill.customerId) warnings.push(`Chưa xác định được khách ERP của "${prefill.crmCustomerName ?? ''}" — chọn khách hàng thủ công.`);
      if (!prefill.saleId) warnings.push('Chưa xác định được nhân viên bán trên ERP — chọn thủ công nếu cần.');
      const lines: EditorLine[] = [];
      for (const item of prefill.items) {
        if (!item.productId) {
          warnings.push(`"${item.name}": chưa có mã sản phẩm ERP — thêm tay.`);
          continue;
        }
        let lots: PrefillLot[] = [];
        try {
          lots = await loadLots(item.productId);
        } catch {
          warnings.push(`"${item.name}": không tải được tồn kho — thêm tay.`);
          continue;
        }
        const { allocs, missing } = allocateLots(item, lots);
        for (const a of allocs) {
          lines.push({
            key: `crm-${prefill.draftId}-${a.lot.id}`,
            productId: Number(a.lot.productId ?? item.productId),
            productDetailId: Number(a.lot.productDetailId),
            sku: String(a.lot.sku),
            quantity: a.quantity,
            price: Number(item.price),
            salePrice: Number(a.lot.salePrice ?? 0),
            discountPercent: Number(item.discountPercent ?? 0),
            discountAmount: undefined,
            vat: 0,
            warehouseId: undefined,
            shelf: '',
            expiryDate: a.lot.expiryDate ? String(a.lot.expiryDate).slice(0, 10) : '',
            isGift: false,
            productName: String(a.lot.productName ?? item.name),
            detailName: String(a.lot.detailName ?? ''),
            unitName: String(a.lot.unitName ?? item.unit ?? ''),
            availableQuantity: Number(a.lot.quantity ?? 0),
            inPrice: Number(a.lot.inPrice ?? 0),
            minPrice: Number(a.lot.minPrice ?? 0)
          });
        }
        if (allocs.length > 1) warnings.push(`"${item.name}": chia ${allocs.length} lô (hạn dùng gần nhất trước).`);
        if (missing > 0) warnings.push(`"${item.name}": THIẾU TỒN ${missing} ${item.unit ?? ''} — kiểm tra lại trước khi lưu.`);
      }
      if (prefill.vatInvoice) warnings.push('Khách lấy hóa đơn VAT — kiểm tra ô VAT.');
      if (cancelled) return;
      setState((current) => ({ ...current, lines }));
      setPrefillInfo({ code: prefill.code, warnings, loading: false });
    })();
    return () => {
      cancelled = true;
    };
  }, [prefill, invoiceId, outbound]);
  const [loading, setLoading] = useState(Boolean(invoiceId));
  const [saving, setSaving] = useState(false);
  const [popupError, setPopupError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [priceSuggestions, setPriceSuggestions] = useState<Record<string, number[]>>({});

  useEffect(() => {
    document.body.classList.add('drawer-open');
    return () => document.body.classList.remove('drawer-open');
  }, []);

  useEffect(() => {
    if (!invoiceId) return;
    const controller = new AbortController();
    setLoading(true);
    api<{ invoice: Record<string, unknown>; lines: Array<Record<string, unknown>> }>(
      `/api/invoices/${kind}/${invoiceId}`,
      {},
      controller.signal
    )
      .then(({ invoice, lines }) => {
        setState({
          partnerId: Number(outbound ? invoice.customer_id : invoice.distributor_id),
          partnerLabel: String(invoice.partnerName ?? ''),
          discountTypeId: invoice.discount_type_id ? Number(invoice.discount_type_id) : undefined,
          discountTypeLabel: String(invoice.discountTypeName ?? ''),
          shipperId: invoice.shipper_id ? Number(invoice.shipper_id) : undefined,
          shipperLabel: String(invoice.shipperName ?? ''),
          saleId: invoice.sale_id ? Number(invoice.sale_id) : undefined,
          saleLabel: String(invoice.saleName ?? ''),
          date: String(invoice.date ?? '').slice(0, 10),
          deliveryDate: outbound ? String(invoice.deliveryDate ?? '') : '',
          discountValue: Number(invoice.discount_value ?? 0),
          paid: Number(invoice.paid ?? 0),
          paidDebt: Number(invoice.paid_debt ?? 0),
          vat: Number(invoice.vat ?? 0),
          deliveryStatus: Number(invoice.is_delivery ?? 0),
          note: String(invoice.note ?? ''),
          updatedAt: String(invoice.updatedAt ?? ''),
          updateBlockedReason:
            !outbound && isInvoicePaidInFull(
              Number(invoice.total ?? 0),
              Number(invoice.paid ?? 0),
              Number(invoice.paid_debt ?? 0)
            )
              ? 'Hóa đơn nhập đã thanh toán đủ, không thể cập nhật.'
              : '',
          lines: lines.map((line, index) => ({
            key: `existing-${line.id ?? index}`,
            id: String(line.id ?? ''),
            productId: Number(line.productId),
            productDetailId: Number(line.productDetailId),
            sku: String(line.sku ?? ''),
            quantity: Number(line.quantity),
            price: Number(!outbound ? line.referencePrice ?? line.price : line.price),
            salePrice: Number(line.salePrice ?? 0),
            discountPercent: Number(line.discountPercent ?? 0),
            discountAmount: outbound ? Number(line.discountAmount ?? 0) : undefined,
            vat: Number(line.vat ?? 0),
            warehouseId: line.warehouseId ? Number(line.warehouseId) : undefined,
            shelf: String(line.shelf ?? ''),
            expiryDate: String(line.expiryDate ?? ''),
            isGift: Boolean(line.isGift),
            productName: String(line.productName ?? ''),
            detailName: String(line.detailName ?? ''),
            unitName: String(line.unitName ?? ''),
            availableQuantity: Number(line.availableQuantity ?? 0),
            inPrice: Number(line.inPrice ?? 0),
            warehouseName: String(line.warehouseName ?? ''),
            discountTypeProductId: line.discountTypeProductId
              ? Number(line.discountTypeProductId)
              : undefined,
            discountTypeProductLabel: String(line.discountTypeProductLabel ?? ''),
            discountTypeProductPercent:
              line.discountTypeProductPercent === null || line.discountTypeProductPercent === undefined
                ? undefined
                : Number(line.discountTypeProductPercent)
          }))
        });
      })
      .catch((reason) => {
        if (!controller.signal.aborted) setPopupError(reason instanceof Error ? reason.message : 'Không thể tải hóa đơn.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [invoiceId, kind, outbound]);

  const calculation = useMemo(
    () =>
      outbound
        ? calculateOutbound(state.lines, state.vat, state.paid, state.paidDebt)
        : calculateInbound(state.lines, state.paid, state.paidDebt),
    [outbound, state.lines, state.paid, state.paidDebt, state.vat]
  );

  function patchLine(key: string, patch: Partial<EditorLine>) {
    setState((current) => ({
      ...current,
      lines: current.lines.map((line) => (line.key === key ? { ...line, ...patch } : line))
    }));
  }

  function clearFieldError(key: string) {
    setFieldErrors((current) => {
      if (!current[key]) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  }

  function patchProductDiscount(productId: number, item: OptionItem | null) {
    const discount = productDiscountFromOption(item);
    const affectedErrorKeys = new Set(
      state.lines
        .filter((line) => line.productId === productId)
        .map((line) => `productDiscount:${line.key}`)
    );
    setFieldErrors((current) =>
      Object.fromEntries(Object.entries(current).filter(([key]) => !affectedErrorKeys.has(key)))
    );
    setState((current) => ({
      ...current,
      lines: current.lines.map((line) =>
        line.productId === productId
          ? {
              ...line,
              discountTypeProductId: discount.id,
              discountTypeProductLabel: discount.label,
              discountTypeProductPercent: discount.percent
            }
          : line
      )
    }));
  }

  async function loadPriceSuggestions(line: EditorLine) {
    if (outbound || Object.hasOwn(priceSuggestions, line.key)) return;
    setPriceSuggestions((current) => ({ ...current, [line.key]: [] }));
    try {
      const result = await api<{ items: Array<{ price: number }> }>(
        `/api/options/inbound-prices?productDetailId=${line.productDetailId}&limit=10&page=0`
      );
      setPriceSuggestions((current) => ({
        ...current,
        [line.key]: [...new Set(result.items.map((item) => Number(item.price)).filter((price) => price > 0))]
      }));
    } catch {
      setPriceSuggestions((current) => ({ ...current, [line.key]: [] }));
    }
  }

  function addProduct(item: OptionItem | null) {
    if (!item) return;
    const sku = outbound ? String(item.sku ?? '') : undefined;
    const detailId = Number(item.productDetailId ?? item.id);
    const duplicate = state.lines.some((line) =>
      outbound ? line.sku === sku : line.productDetailId === detailId && !line.isGift
    );
    if (duplicate) {
      setFieldErrors((current) => ({ ...current, product: 'Sản phẩm này đã có trong hóa đơn.' }));
      return;
    }
    clearFieldError('product');
    clearFieldError('lines');
    const existingProductLine = state.lines.find((line) => line.productId === Number(item.productId));
    const productDiscount = resolveProductDiscount(
      existingProductLine
        ? {
            id: existingProductLine.discountTypeProductId,
            label: existingProductLine.discountTypeProductLabel ?? '',
            percent: existingProductLine.discountTypeProductPercent
          }
        : undefined,
      {
        id: item.discountTypeProductId ? Number(item.discountTypeProductId) : undefined,
        label: String(item.discountTypeProductLabel ?? ''),
        percent:
          item.discountTypeProductPercent === null || item.discountTypeProductPercent === undefined
            ? undefined
            : Number(item.discountTypeProductPercent)
      }
    );
    const line: EditorLine = {
      key: `${Date.now()}-${item.id}`,
      productId: Number(item.productId),
      productDetailId: detailId,
      sku,
      quantity: 1,
      price: Number(outbound ? item.salePrice ?? 0 : item.importPrice ?? 0),
      salePrice: Number(item.salePrice ?? 0),
      discountPercent: 0,
      discountAmount: outbound ? 0 : undefined,
      vat: Number(item.vat ?? 0),
      warehouseId: undefined,
      shelf: '',
      expiryDate: item.expiryDate ? String(item.expiryDate).slice(0, 10) : '',
      isGift: false,
      productName: String(item.productName ?? ''),
      detailName: String(item.detailName ?? ''),
      unitName: String(item.unitName ?? ''),
      availableQuantity: Number(item.quantity ?? 0),
      inPrice: Number(item.inPrice ?? 0),
      minPrice: Number(item.minPrice ?? 0),
      discountTypeProductId: productDiscount.id,
      discountTypeProductLabel: productDiscount.label,
      discountTypeProductPercent: productDiscount.percent
    };
    setState((current) => ({ ...current, lines: [...current.lines, line] }));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (state.updateBlockedReason) {
      setPopupError(state.updateBlockedReason);
      return;
    }
    const nextErrors: FieldErrors = {};
    if (!state.partnerId) nextErrors.partner = `Vui lòng chọn ${outbound ? 'khách hàng' : 'nhà phân phối'}.`;
    if (state.lines.length === 0) nextErrors.lines = 'Vui lòng thêm ít nhất một sản phẩm.';
    if (state.lines.length > 200) nextErrors.lines = 'Mỗi hóa đơn chỉ được tối đa 200 dòng sản phẩm.';
    state.lines.forEach((line) => {
      if (line.quantity < 1) nextErrors[`quantity:${line.key}`] = 'Số lượng phải từ 1.';
      if (line.price < 0) nextErrors[`price:${line.key}`] = 'Đơn giá không được âm.';
      if (!line.isGift && line.price <= 0) {
        nextErrors[`price:${line.key}`] = `${outbound ? 'Giá bán' : 'Giá nhập'} phải lớn hơn 0.`;
      }
      if (!line.isGift && outbound && line.price < (line.minPrice ?? 0)) {
        nextErrors[`price:${line.key}`] = `Tối thiểu ${money(line.minPrice ?? 0)} ₫.`;
      }
      if (outbound && line.quantity > (line.availableQuantity ?? 0)) {
        nextErrors[`quantity:${line.key}`] = `Tồn khả dụng ${money(line.availableQuantity ?? 0)}.`;
      }
      if ((line.discountPercent ?? 0) < 0 || (line.discountPercent ?? 0) > 100) {
        nextErrors[`discount:${line.key}`] = 'Chiết khấu từ 0–100%.';
      }
      if (outbound && line.discountAmount !== undefined && (line.discountAmount < 0 || line.discountAmount > (line.isGift ? 0 : line.price * line.quantity))) {
        nextErrors[`discountAmount:${line.key}`] = 'Tiền CK không được vượt quá thành tiền trước CK.';
      }
      if (!outbound && !line.discountTypeProductId) {
        nextErrors[`productDiscount:${line.key}`] = 'Vui lòng chọn loại chiết khấu sản phẩm.';
      }
    });
    if (calculation.remaining < 0) nextErrors.payment = 'Số tiền đã thanh toán không được lớn hơn tổng tiền.';
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      window.requestAnimationFrame(() => {
        const firstInvalid = document.querySelector<HTMLElement>('.invoice-drawer [aria-invalid="true"]');
        firstInvalid?.scrollIntoView({ block: 'nearest' });
        firstInvalid?.focus();
      });
      return;
    }
    setSaving(true);
    setPopupError('');
    const payload = {
      [outbound ? 'customerId' : 'distributorId']: state.partnerId,
      date: state.date,
      discountTypeId: state.discountTypeId,
      discountValue: state.discountValue,
      paid: state.paid,
      note: state.note,
      updatedAt: state.updatedAt,
      ...(outbound
        ? {
            shipperId: state.shipperId,
            saleId: state.saleId,
            vat: state.vat,
            deliveryStatus: state.deliveryStatus,
            deliveryDate: state.deliveryDate
          }
        : {}),
      lines: state.lines.map(({ key: _key, productName: _productName, detailName: _detailName, unitName: _unitName, availableQuantity: _available, inPrice: _inPrice, minPrice: _minPrice, warehouseName: _warehouseName, discountTypeProductLabel: _discountTypeProductLabel, discountTypeProductPercent: _discountTypeProductPercent, ...line }) => line)
    };
    try {
      const saved = await api<{ invoice?: { id?: string | number; sku?: string; invoiceNumber?: number } }>(`/api/invoices/${kind}${invoiceId ? `/${invoiceId}` : ''}`, {
        method: invoiceId ? 'PUT' : 'POST',
        body: JSON.stringify(payload)
      });
      if (prefill && !invoiceId && saved?.invoice?.id) {
        notifyParent({ type: 'crm-draft-saved', draftId: prefill.draftId, invoiceId: Number(saved.invoice.id), sku: saved.invoice.sku ?? '', invoiceNumber: saved.invoice.invoiceNumber ?? null });
      }
      onSaved(`${invoiceId ? 'Cập nhật' : 'Tạo'} hóa đơn ${outbound ? 'xuất' : 'nhập'} thành công.`);
    } catch (reason) {
      setPopupError(reason instanceof Error ? reason.message : 'Không thể lưu hóa đơn.');
    } finally {
      setSaving(false);
    }
  }

  const documentDateField = (
    <div className="field">
      <label htmlFor="invoice-date">Ngày chứng từ</label>
      <input id="invoice-date" type="date" value={state.date} onChange={(event) => setState((current) => ({ ...current, date: event.target.value }))} required />
    </div>
  );
  const invoiceDiscountField = (
    <AsyncSelect
      label="Loại CK hóa đơn"
      path="/api/options/discount-types"
      value={state.discountTypeId}
      selectedLabel={state.discountTypeLabel}
      placeholder="Không áp dụng"
      onChange={(item) => setState((current) => ({ ...current, discountTypeId: item ? Number(item.id) : undefined, discountTypeLabel: String(item?.label ?? '') }))}
    />
  );

  return (
    <div className="drawer-backdrop" role="presentation">
      <section className="invoice-drawer is-fullscreen" role="dialog" aria-modal="true" aria-labelledby="editor-title">
        <header className="drawer-header">
          <h2 id="editor-title">{invoiceId ? `Cập nhật hóa đơn ${outbound ? 'xuất' : 'nhập'}` : `Thêm hóa đơn ${outbound ? 'xuất' : 'nhập'}`}</h2>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Đóng"><CloseIcon /></button>
        </header>
        {popupError ? (
          <div className="editor-popup" role="alert">
            <span>{popupError}</span>
            <button type="button" onClick={() => setPopupError('')} aria-label="Đóng thông báo"><CloseIcon /></button>
          </div>
        ) : null}

        {loading ? <div className="editor-loading"><span className="spinner" /> Đang tải…</div> : (
          <form onSubmit={submit}>
            {state.updateBlockedReason ? <div className="editor-blocked" role="alert">{state.updateBlockedReason}</div> : null}
            {prefillInfo ? (
              <div className="editor-blocked" role="status" style={{ background: '#EFF6FF', borderColor: '#93C5FD', color: '#1E3A8A' }}>
                <strong>Điền sẵn từ đơn nháp CRM {prefillInfo.code}</strong>
                {prefillInfo.loading ? ' — đang lấy tồn kho từng lô…' : ' — dò lại khách hàng, sản phẩm, số lượng, giá rồi bấm Lưu.'}
                {prefillInfo.warnings.length ? <ul style={{ margin: '6px 0 0 18px' }}>{prefillInfo.warnings.map((w) => <li key={w}>{w}</li>)}</ul> : null}
              </div>
            ) : null}
            <div className="editor-layout">
              <div className="editor-fixed-top">
                <section className={`form-section header-fields ${outbound ? 'outbound-header-fields' : 'inbound-header-fields'}`}>
                <AsyncSelect
                  label={outbound ? 'Khách hàng' : 'Nhà phân phối'}
                  path={`/api/options/${outbound ? 'customers' : 'distributors'}`}
                  value={state.partnerId}
                  selectedLabel={state.partnerLabel}
                  placeholder={outbound ? 'Nhập tên hoặc số điện thoại' : 'Nhập tên hoặc số điện thoại nhà phân phối'}
                  error={fieldErrors.partner}
                  minimumQueryLength={2}
                  pageSize={30}
                  onChange={(item) => {
                    clearFieldError('partner');
                    if (!outbound) clearFieldError('lines');
                    setState((current) => ({
                      ...current,
                      partnerId: item ? Number(item.id) : undefined,
                      partnerLabel: String(item?.label ?? ''),
                      ...(!outbound ? { lines: [] } : {})
                    }));
                  }}
                  renderOption={(item) => <div className="partner-option"><strong>{String(item.label ?? '')}</strong>{item.phone ? <span>{String(item.phone)}</span> : null}</div>}
                />
                {outbound ? <div className="header-date-discount">{documentDateField}{invoiceDiscountField}</div> : <>{documentDateField}{invoiceDiscountField}</>}
                {outbound ? (
                  <div className="field"><label htmlFor="delivery-date">Ngày giao dự kiến</label><input id="delivery-date" type="date" value={state.deliveryDate} onChange={(event) => setState((current) => ({ ...current, deliveryDate: event.target.value }))} /></div>
                ) : null}
                {outbound ? (
                  <div className="field">
                    <label htmlFor="delivery">Trạng thái giao hàng</label>
                    <select id="delivery" value={state.deliveryStatus} onChange={(event) => setState((current) => ({ ...current, deliveryStatus: Number(event.target.value) }))}>
                      {deliveryStatusOptions(state.deliveryStatus).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                  </div>
                ) : null}
                {outbound ? (
                  <AsyncSelect label="Nhân viên bán" path="/api/options/staff?role=sale" value={state.saleId} selectedLabel={state.saleLabel} placeholder="Chọn nếu có" onChange={(item) => setState((current) => ({ ...current, saleId: item ? Number(item.id) : undefined, saleLabel: String(item?.label ?? '') }))} />
                ) : null}
                {outbound ? (
                  <AsyncSelect label="Người giao hàng" path="/api/options/staff?role=shipper" value={state.shipperId} selectedLabel={state.shipperLabel} placeholder="Chọn nếu có" onChange={(item) => setState((current) => ({ ...current, shipperId: item ? Number(item.id) : undefined, shipperLabel: String(item?.label ?? '') }))} />
                ) : null}
                </section>
              </div>

              <section className="line-section">
                <div className="section-heading">
                  <h3>Chi tiết hàng hóa ({state.lines.length})</h3>
                </div>
                <div className="product-search-row">
                  <AsyncSelect
                    label="Thêm sản phẩm"
                    path={outbound ? '/api/options/stock-lots' : `/api/options/inbound-products?distributorId=${state.partnerId ?? ''}`}
                    disabled={!outbound && !state.partnerId}
                    placeholder={outbound ? 'Nhập tên, UPC hoặc mã lô' : 'Nhập tên, UPC hoặc phân loại'}
                    error={fieldErrors.product ?? fieldErrors.lines}
                    minimumQueryLength={2}
                    pageSize={30}
                    onChange={addProduct}
                    renderOption={(item) => (
                      <div className="product-option">
                        <strong>{String(item.productName ?? '')}{visibleProductDetailName(String(item.productName ?? ''), String(item.detailName ?? '')) ? <> <em>{String(item.detailName ?? '')}</em></> : null}</strong>
                        <span>{outbound ? `${item.sku} · Tồn ${item.quantity}` : `${item.upc} · ${item.unitName ?? ''}`}</span>
                      </div>
                    )}
                  />
                  <button type="button" className="secondary-action add-hint" onClick={() => document.querySelector<HTMLInputElement>('.product-search-row input')?.focus()}><PlusIcon /> Thêm</button>
                </div>

                <div className="editor-table-wrap">
                  <table className={`editor-table ${outbound ? 'outbound-editor-table' : 'inbound-editor-table'}`}>
                    <thead><tr><th>Sản phẩm / lô</th><th>SL</th><th>{outbound ? 'Giá bán' : 'Giá nhập'}</th>{!outbound ? <th>Giá bán</th> : null}<th title={outbound ? 'Khi nhập tiền CK, tỷ lệ % được làm tròn để tham khảo; số tiền CK là giá trị tính chính xác.' : 'Phần trăm chiết khấu trực tiếp của dòng hóa đơn'}>CK % dòng</th>{outbound ? <th title="Số tiền chiết khấu chính xác của dòng hóa đơn">Tiền CK</th> : null}{!outbound ? <><th title="Nhóm chiết khấu mặc định của sản phẩm dùng khi bán">Loại CK sản phẩm *</th><th>Kho</th><th>Kệ</th></> : <th>Tồn khả dụng</th>}<th>Thành tiền</th><th /></tr></thead>
                    <tbody>
                      {state.lines.length === 0 ? (
                        <tr><td colSpan={outbound ? 8 : 10}><div className="empty-lines"><strong>Chưa có sản phẩm</strong></div></td></tr>
                      ) : state.lines.map((line, index) => {
                        const lineTotal = calculation.lines[index]?.total ?? 0;
                        return (
                          <tr key={line.key}>
                            <td className="line-product"><strong>{line.productName}</strong><span>{productSecondaryText(line.productName, line.detailName, line.unitName, line.sku, outbound && line.expiryDate ? `HSD ${line.expiryDate}` : '')}</span><label className="gift-toggle"><input type="checkbox" checked={line.isGift ?? false} onChange={(event) => { const isGift = event.target.checked; const duplicate = !outbound && state.lines.some((item) => item.key !== line.key && item.productDetailId === line.productDetailId && Boolean(item.isGift) === isGift); if (duplicate) { setFieldErrors((current) => ({ ...current, product: `Phân loại ${line.detailName || line.productName} đã có một dòng ${isGift ? 'quà tặng' : 'hàng nhập'}.` })); return; } if (isGift) { clearFieldError(`price:${line.key}`); clearFieldError(`discountAmount:${line.key}`); } else if (line.price <= 0) setFieldErrors((current) => ({ ...current, [`price:${line.key}`]: `${outbound ? 'Giá bán' : 'Giá nhập'} phải lớn hơn 0.` })); patchLine(line.key, { isGift, ...(outbound && isGift ? { discountPercent: 0, discountAmount: 0 } : {}) }); }} /> Quà tặng</label></td>
                            <td><div className="cell-field">{numberField(line.quantity, (value) => { clearFieldError(`quantity:${line.key}`); patchLine(line.key, { quantity: value }); }, 'Số lượng', { min: 1, error: fieldErrors[`quantity:${line.key}`] })}{fieldErrors[`quantity:${line.key}`] ? <span className="field-error" role="alert">{fieldErrors[`quantity:${line.key}`]}</span> : null}</div></td>
                            <td><div className="cell-field">{numberField(line.price, (value) => { clearFieldError(`price:${line.key}`); patchLine(line.key, { price: value }); }, outbound ? 'Giá bán' : 'Giá nhập', { money: true, error: fieldErrors[`price:${line.key}`], list: outbound ? undefined : `price-history-${line.key}`, onFocus: outbound ? undefined : () => void loadPriceSuggestions(line) })}{!outbound ? <datalist id={`price-history-${line.key}`}>{(priceSuggestions[line.key] ?? []).map((price) => <option key={price} value={money(price)}>{money(price)} ₫</option>)}</datalist> : null}{fieldErrors[`price:${line.key}`] ? <span className="field-error" role="alert">{fieldErrors[`price:${line.key}`]}</span> : outbound && line.minPrice ? <small>Min {money(line.minPrice)}</small> : !outbound && (priceSuggestions[line.key]?.length ?? 0) > 0 ? <small>{priceSuggestions[line.key]!.length} giá nhập gần đây</small> : null}</div></td>
                            {!outbound ? <td>{numberField(line.salePrice ?? 0, (value) => patchLine(line.key, { salePrice: value }), 'Giá bán', { money: true })}</td> : null}
                            <td><div className="cell-field">{numberField(outbound ? calculation.lines[index]?.discountPercent ?? 0 : line.discountPercent ?? 0, (value) => { clearFieldError(`discount:${line.key}`); clearFieldError(`discountAmount:${line.key}`); patchLine(line.key, { discountPercent: value, ...(outbound ? { discountAmount: undefined } : {}) }); }, 'Chiết khấu phần trăm', { min: 0, max: 100, error: fieldErrors[`discount:${line.key}`] })}{fieldErrors[`discount:${line.key}`] ? <span className="field-error" role="alert">{fieldErrors[`discount:${line.key}`]}</span> : null}</div></td>
                            {outbound ? <td><div className="cell-field">{numberField(calculation.lines[index]?.discountAmount ?? 0, (value) => { clearFieldError(`discountAmount:${line.key}`); patchLine(line.key, { discountAmount: value }); }, 'Tiền chiết khấu', { money: true, error: fieldErrors[`discountAmount:${line.key}`] })}{fieldErrors[`discountAmount:${line.key}`] ? <span className="field-error" role="alert">{fieldErrors[`discountAmount:${line.key}`]}</span> : null}</div></td> : null}
                            {!outbound ? <td className="product-discount-cell"><AsyncSelect label="Loại chiết khấu sản phẩm" path="/api/options/discount-type-products" value={line.discountTypeProductId} selectedLabel={productDiscountLabel(line.discountTypeProductLabel, line.discountTypeProductPercent)} placeholder="Chọn loại" error={fieldErrors[`productDiscount:${line.key}`]} onChange={(item) => patchProductDiscount(line.productId, item)} renderOption={(item) => <div className="discount-option"><strong>{String(item.label ?? '')}</strong><span>{percent(item.discountPercent === null || item.discountPercent === undefined ? undefined : Number(item.discountPercent))}% mặc định khi bán</span></div>} /><small>Áp dụng cho mọi phân loại cùng sản phẩm</small></td> : null}
                            {!outbound ? <><td><AsyncSelect label="" path="/api/options/warehouses" value={line.warehouseId} selectedLabel={line.warehouseName || (line.warehouseId ? `Kho #${line.warehouseId}` : '')} placeholder="Không bắt buộc" onChange={(item) => patchLine(line.key, { warehouseId: item ? Number(item.id) : undefined, warehouseName: String(item?.label ?? '') })} /></td><td><input aria-label="Kệ" value={line.shelf ?? ''} onChange={(event) => patchLine(line.key, { shelf: event.target.value })} placeholder="Không bắt buộc" /></td></> : <td><span className={line.quantity > (line.availableQuantity ?? 0) ? 'stock-warning' : 'stock-ok'}>{line.availableQuantity ?? 0}</span></td>}
                            <td className="line-total">{money(lineTotal)} ₫</td>
                            <td><button type="button" className="remove-line" aria-label="Xóa dòng" onClick={() => setState((current) => ({ ...current, lines: current.lines.filter((item) => item.key !== line.key) }))}><TrashIcon /></button></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="form-bottom">
                <div className="field note-field"><label htmlFor="note">Ghi chú</label><textarea id="note" value={state.note} onChange={(event) => setState((current) => ({ ...current, note: event.target.value }))} maxLength={1000} /></div>
                <div className="settlement">
                  <div><span>Tạm tính</span><b>{money(calculation.subtotal)} ₫</b></div>
                  <div><span>Chiết khấu dòng</span><b>− {money(calculation.discount)} ₫</b></div>
                  <div className="paid-line"><span>CK toàn hóa đơn</span>{numberField(state.discountValue, (value) => setState((current) => ({ ...current, discountValue: value })), 'Chiết khấu toàn hóa đơn', { money: true })}<em>₫</em></div>
                  {outbound ? <div className="vat-line"><span>VAT</span><label>{numberField(state.vat, (value) => setState((current) => ({ ...current, vat: value })), 'VAT', { min: 0, max: 100 })}<em>%</em></label><b>+ {money(calculation.vatAmount)} ₫</b></div> : null}
                  <div className="grand-total"><span>Tổng thanh toán</span><b>{money(calculation.total)} ₫</b></div>
                  <div className="paid-line"><span>Trả trước</span>{numberField(state.paid, (value) => { clearFieldError('payment'); setState((current) => ({ ...current, paid: value })); }, 'Trả trước', { money: true, error: fieldErrors.payment })}<em>₫</em></div>
                  {state.paidDebt > 0 ? <div><span>Đã trả công nợ</span><b>{money(state.paidDebt)} ₫</b></div> : null}
                  <div className="remaining"><span>Còn lại</span><b className={calculation.remaining < 0 ? 'negative' : ''}>{money(calculation.remaining)} ₫</b></div>
                  {fieldErrors.payment ? <div className="settlement-validation field-error" role="alert">{fieldErrors.payment}</div> : null}
                </div>
              </section>
            </div>
            <footer className="drawer-footer">
              <button type="button" className="secondary-action" onClick={onClose}>Hủy</button>
              <button className="primary-action" disabled={saving || Boolean(state.updateBlockedReason)}>{saving ? <span className="button-spinner" /> : invoiceId ? <ArrowUpIcon /> : <ArrowDownIcon />} {saving ? 'Đang lưu…' : invoiceId ? 'Lưu' : `Thêm hóa đơn ${outbound ? 'xuất' : 'nhập'}`}</button>
            </footer>
          </form>
        )}
      </section>
    </div>
  );
}
